import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn, getTestId } from '../utils';
import { Icon } from './icons/Icon';
import { IconName } from '../icons/names';

type baseMenuButtonAttributes = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'onClick' | 'onFocus' | 'id'
>;

const MenuButtonVariants = cva(
  `
    transition-all
    rounded-lg
    flex items-center flex-nowrap font-regular
    cursor-pointer
    select-none
    `,
  {
    variants: {
      variant: {
        inactive: 'bg-transparent text-foreground-base hover:bg-background-muted',
        active: 'bg-secondary-base text-foreground-white'
      },
      orientation: {
        vertical:
          'min-w-[100px] min-h-[70px] flex-col justify-center pb-xs pt-sm p-xs text-md hover:scale-[1.02] active:scale-[0.98]',
        horizontal:
          'min-w-[200px] min-h-[50px] flex-row justify-start text-left text-md hover:scale-[1.02] active:scale-[0.98]',
        sidebar: 'flex-row gap-sm px-md py-sm text-sm font-medium'
      }
    },
    defaultVariants: {
      variant: 'inactive',
      orientation: 'vertical'
    },
    compoundVariants: [
      {
        orientation: 'sidebar',
        variant: 'active',
        className: 'bg-background-muted text-secondary-base'
      },
      {
        orientation: 'sidebar',
        variant: 'inactive',
        className: 'hover:bg-background-muted'
      }
    ]
  }
);

type MenuButtonVariantProps = VariantProps<typeof MenuButtonVariants>;

export interface MenuButtonProps extends baseMenuButtonAttributes, MenuButtonVariantProps {
  className?: string;
  /** Icon name or custom ReactNode. Defaults to 'house' for vertical/horizontal, hidden for sidebar. */
  icon?: IconName | React.ReactNode;
  children: string;
  testId?: string;
}

const isIconName = (value: unknown): value is IconName => typeof value === 'string';

export const MenuButton = React.forwardRef<HTMLDivElement, MenuButtonProps>(
  ({ className, children, variant, icon, orientation, onClick, testId, id, ...props }, ref) => {
    const isSidebar = orientation === 'sidebar';
    const resolvedIcon = icon ?? (isSidebar ? undefined : 'house');

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
      }
    };

    const renderIcon = () => {
      if (resolvedIcon == null) return null;
      if (isIconName(resolvedIcon)) {
        return (
          <Icon
            name={resolvedIcon}
            className={isSidebar ? '' : orientation === 'horizontal' ? 'ms-md' : 'mt-xs'}
          />
        );
      }
      return resolvedIcon;
    };

    return (
      <div
        ref={ref}
        role='button'
        tabIndex={0}
        id={id}
        data-testid={getTestId({ testId, id })}
        {...props}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={cn(MenuButtonVariants({ variant, orientation }), className)}
      >
        {renderIcon()}

        <h2
          className={cn(
            isSidebar ? '' : orientation === 'horizontal' ? 'ms-md' : 'w-[90%] mt-xs',
            !isSidebar && 'text-xs text-center'
          )}
        >
          {children}
        </h2>
      </div>
    );
  }
);

MenuButton.displayName = 'MenuButton';
